import { HttpParams } from '@angular/common/http';
import { Directive, OnDestroy } from '@angular/core';
import { SCData } from '@app/models/core/SCData';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { getLazyLoadingParams } from './apihelper';

export interface SubscriptionConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  destroyObj?: any;
  filterUndefined?: boolean;
  subscriptionIndex?: string;
  setSubmitted?: boolean;
  shouldUnsubscribe?: boolean;
}

export interface HeaderSubscriptionConfig extends SubscriptionConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: HttpParams | any;
}

export class NameDirective {
  constructor() {}
}
@UntilDestroy()
@Directive()
export class BaseClass implements OnDestroy {
  protected subscriptions = new Subscription();

  protected runningSubscriptions: {
    [subscriptionIndex: string]: { isRunning: boolean };
  } = {};
  protected dataSourceSubscriptions: { [index: string]: Subscription } = {};

  public getLazyLoadingParams = getLazyLoadingParams;
  protected isSubmitted = false;

  constructor() {}
  ngOnDestroy() {}

  isSubscriptionRunning(subscriptionIndex: string) {
    return (
      this.runningSubscriptions[subscriptionIndex] != undefined &&
      this.runningSubscriptions[subscriptionIndex].isRunning
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addSubscription<T = any>(
    sub: Observable<T>,
    config: SubscriptionConfig = {},
  ): Observable<T | undefined> {
    const destroyObj =
      config.destroyObj != undefined ? config.destroyObj : this;
    const filterUndefined =
      config.filterUndefined != undefined ? config.filterUndefined : true;
    const subscriptionIndex =
      config.subscriptionIndex != undefined
        ? config.subscriptionIndex
        : undefined;
    const setSubmitted =
      config.subscriptionIndex != undefined ? config.setSubmitted : false;
    sub = sub.pipe(untilDestroyed(destroyObj));
    if (filterUndefined) {
      sub = sub.pipe(filter((resp) => resp != undefined));
    }
    if (subscriptionIndex) {
      if (this.isSubscriptionRunning(subscriptionIndex)) {
        return of(undefined).pipe(
          untilDestroyed(destroyObj),
          filter((resp) => resp != undefined),
        );
      }
      this.runningSubscriptions[subscriptionIndex] = {
        isRunning: true,
      };
      sub = sub.pipe(
        catchError((err) => {
          if (subscriptionIndex) {
            this.runningSubscriptions[subscriptionIndex].isRunning = false;
          }
          throw err;
        }),
        map((resp) => {
          if (subscriptionIndex) {
            this.runningSubscriptions[subscriptionIndex].isRunning = false;
          }
          return resp;
        }),
      );
    }

    if (setSubmitted) {
      sub = sub.pipe(
        map((resp) => {
          this.isSubmitted = true;
          return resp;
        }),
      );
    }
    return sub;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  addHeaderSubscription<T = any>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sub: Observable<any>,
    config: HeaderSubscriptionConfig = {},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    const destroyObj =
      config.destroyObj != undefined ? config.destroyObj : this;
    const params = config.params != undefined ? config.params : undefined;
    sub = this.addSubscription(sub, config);
    sub = sub.pipe(
      filter((event) => {
        let componentOK = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let source: any = event.source;
        while (source && !componentOK) {
          componentOK =
            source.component != undefined &&
            destroyObj instanceof source.component;
          source = source.parent;
        }
        if (!componentOK) {
          //JC TODO Might break things, needs some testing
          source = event.source;
          while (source && !componentOK) {
            componentOK =
              source.component != undefined &&
              destroyObj instanceof source.component;
            source = source.firstChild;
          }
        }
        return componentOK;
      }),
    );
    if (params) {
      const paramString = JSON.stringify(params);
      sub = sub.pipe(
        filter(
          (event) =>
            JSON.stringify(event.source.snapshot.params) == paramString,
        ),
      );
    }
    return sub;
  }

  preventEvent(event: MouseEvent) {
    event.preventDefault();
    // EDIT: Looks like you also have to include Event#stopImmediatePropagation as well
    event.stopImmediatePropagation();
  }

  //read resp.data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSCData<T = any>(
    dataSubject: Subject<T | undefined>,
    dataSourceObs: Observable<SCData<T>>,
    config: SubscriptionConfig = {},
  ) {
    const subscriptionIndex =
      config.subscriptionIndex != undefined
        ? config.subscriptionIndex
        : undefined;
    const shouldUnsubscribe =
      config.shouldUnsubscribe != undefined ? config.shouldUnsubscribe : true;
    dataSubject.next(undefined);
    if (subscriptionIndex && this.dataSourceSubscriptions[subscriptionIndex]) {
      this.dataSourceSubscriptions[subscriptionIndex].unsubscribe();
    }

    const subcription = this.addSubscription(dataSourceObs).subscribe(
      (resp) => {
        if (resp) {
          dataSubject.next(resp.data);
          if (shouldUnsubscribe) {
            subcription.unsubscribe();
          }
        }
      },
    );

    if (subscriptionIndex) {
      this.dataSourceSubscriptions[subscriptionIndex] = subcription;
    }
    return dataSubject.asObservable();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPagingData<T = any>(
    dataSubject: Subject<T | undefined>,
    dataSourceObs: Observable<T>,
    config: SubscriptionConfig = {},
  ) {
    return this.getDataHelper(dataSubject, dataSourceObs, config);
  }

  //reads just resp
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDataHelper<T = any>(
    dataSubject: Subject<T | undefined>,
    dataSourceObs: Observable<T>,
    config: SubscriptionConfig = {},
  ) {
    dataSubject.next(undefined);
    const subscriptionIndex =
      config.subscriptionIndex != undefined
        ? config.subscriptionIndex
        : undefined;
    const shouldUnsubscribe =
      config.shouldUnsubscribe != undefined ? config.shouldUnsubscribe : true;
    if (subscriptionIndex && this.dataSourceSubscriptions[subscriptionIndex]) {
      this.dataSourceSubscriptions[subscriptionIndex].unsubscribe();
    }
    const subcription = this.addSubscription(dataSourceObs).subscribe(
      (resp) => {
        if (resp) {
          dataSubject.next(resp);
          if (shouldUnsubscribe) {
            subcription.unsubscribe();
          }
        }
      },
    );
    if (subscriptionIndex) {
      this.dataSourceSubscriptions[subscriptionIndex] = subcription;
    }
    return dataSubject.asObservable();
  }

  downloadBlob(blob: Blob, name?: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isFirefox = /Gecko\/\d/.test((window.navigator as any).userAgent);
    if (isFirefox && blob.type == 'application/pdf') {
      blob = new Blob([blob], { type: 'application/octet-stream' });
    }
    if (!name) {
      name = 'unnamed.pdf';
    }

    const url = window.URL.createObjectURL(blob);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window.navigator as any).msSaveOrOpenBlob) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window.navigator as any).msSaveBlob(blob, name);
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    window.URL.revokeObjectURL(url);
  }
}
